import React, { useEffect, useState } from 'react';
import { analyticsApi, parentApi } from '../../api/client';
import Spinner from '../../components/common/Spinner';
import StatCard from '../../components/common/StatCard';
import { FaChartBar, FaCalendarCheck, FaTrophy, FaFilePdf, FaGraduationCap } from 'react-icons/fa';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid,
} from 'recharts';

const gradeColor = v => v >= 75 ? '#10b981' : v >= 50 ? '#f59e0b' : '#ef4444';
const gradeSymbol = v => v>=80?'A':v>=70?'B':v>=60?'C':v>=50?'D':v>=40?'E':'F';

export default function ParentAnalytics() {
  const [children,  setChildren]  = useState([]);
  const [selected,  setSelected]  = useState(null);
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [fetching,  setFetching]  = useState(true);

  useEffect(() => {
    parentApi.getChildren()
      .then(r => {
        const list = r.data?.data || [];
        setChildren(list);
        if (list.length > 0) setSelected(list[0]);
      })
      .finally(() => setFetching(false));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    analyticsApi.getParent(selected.user_id)
      .then(r => setData(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selected]);

  const radarData = data?.by_subject?.slice(0,8).map(s => ({
    subject: s.subject_name.length > 10 ? s.subject_name.slice(0,10)+'…' : s.subject_name,
    avg: parseFloat(s.avg_pct) || 0,
  }));

  const openReportCard = () => {
    if (!selected) return;
    const url = analyticsApi.getReportCard(selected.user_id, 0);
    window.open(url, '_blank');
  };

  if (fetching) return <Spinner />;

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div><h2>Children's Performance</h2><p>Detailed academic analytics for your children</p></div>
        <button onClick={openReportCard} disabled={!selected} className="sms-btn sms-btn-outline" style={{color:'#0d6efd'}}>
          <FaFilePdf size={13}/> Report Card
        </button>
      </div>

      {/* Child selector */}
      {children.length > 1 && (
        <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' }}>
          {children.map(c => (
            <button key={c.user_id}
              onClick={() => setSelected(c)}
              className="sms-btn"
              style={{
                background: selected?.user_id===c.user_id ? (c.primary_color||'#0d6efd') : '#fff',
                color: selected?.user_id===c.user_id ? '#fff' : '#374151',
                border: '1px solid #e5e7eb',
              }}>
              <FaGraduationCap size={12}/> {c.first_name} {c.surname}
              <span style={{marginLeft:6,fontSize:'0.75rem',opacity:0.8}}>Gr {c.grade_number}</span>
            </button>
          ))}
        </div>
      )}

      {loading ? <Spinner /> : !data || !data.by_subject?.length ? (
        <div className="sms-card" style={{textAlign:'center',color:'#9ca3af',padding:'60px 0'}}>
          No marks recorded for {selected?.first_name} yet
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:16, marginBottom:28 }}>
            <StatCard label="Overall Average" value={data.overall_avg ? `${data.overall_avg}%` : '—'}
              icon={FaChartBar} color={gradeColor(data.overall_avg)} sub={gradeSymbol(data.overall_avg)} />
            <StatCard label="Attendance" value={`${data.attendance?.pct ?? 0}%`}
              icon={FaCalendarCheck} color={gradeColor(data.attendance?.pct)}
              sub={`${data.attendance?.present}/${data.attendance?.total} days`} />
            <StatCard label="Subjects" value={data.by_subject?.length ?? 0} icon={FaChartBar} color="#8b5cf6" />
            <StatCard label="Assessments" value={data.recent?.length ?? 0} icon={FaTrophy} color="#0d6efd" sub="recent" />
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24, marginBottom:24 }}>
            {/* Subject breakdown */}
            <div className="sms-card">
              <h5 style={{fontWeight:700,marginBottom:16}}>Subject Performance</h5>
              {radarData?.length > 2 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <RadarChart data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="subject" tick={{fontSize:10}} />
                    <Radar dataKey="avg" stroke="#0d6efd" fill="#0d6efd" fillOpacity={0.25} name="Average" />
                    <Tooltip formatter={v=>[`${v}%`,'Avg']} />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <div>
                  {data.by_subject?.map(s => {
                    const avg = parseFloat(s.avg_pct);
                    const col = gradeColor(avg);
                    return (
                      <div key={s.subject_name} style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
                        <div style={{flex:1,fontSize:'0.85rem',fontWeight:500}}>{s.subject_name}</div>
                        <div style={{width:100,height:8,background:'#f3f4f6',borderRadius:4}}>
                          <div style={{width:`${avg}%`,height:'100%',background:col,borderRadius:4}} />
                        </div>
                        <span style={{fontWeight:700,color:col,minWidth:40,textAlign:'right'}}>{avg}%</span>
                        <span style={{fontWeight:800,color:col}}>{gradeSymbol(avg)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Performance trend */}
            <div className="sms-card">
              <h5 style={{fontWeight:700,marginBottom:16}}>Performance Trend (Last 6 Months)</h5>
              {data.trend?.length > 1 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={data.trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" tick={{fontSize:10}} />
                    <YAxis domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{fontSize:10}} />
                    <Tooltip formatter={v=>[`${v}%`,'Avg']} />
                    <Line type="monotone" dataKey="avg_pct" stroke="#0d6efd" strokeWidth={2} dot={{r:4}} name="Average" />
                  </LineChart>
                </ResponsiveContainer>
              ) : <p style={{color:'#9ca3af',textAlign:'center',paddingTop:60}}>Not enough data yet</p>}
            </div>
          </div>

          {/* Recent assessments */}
          <div className="sms-card" style={{padding:0,overflow:'hidden'}}>
            <div style={{padding:'16px 20px',fontWeight:700,borderBottom:'1px solid #e5e7eb'}}>Recent Assessments</div>
            <table className="sms-table">
              <thead><tr><th>Date</th><th>Subject</th><th>Assessment</th><th>Score</th><th>%</th><th>Comment</th></tr></thead>
              <tbody>
                {data.recent?.map((r,i) => {
                  const col = gradeColor(parseFloat(r.pct));
                  return (
                    <tr key={i}>
                      <td style={{color:'#6b7280',fontSize:'0.82rem'}}>{r.date||'—'}</td>
                      <td style={{fontWeight:600}}>{r.subject_name}</td>
                      <td>{r.name}</td>
                      <td>{r.score}/{r.total_marks}</td>
                      <td><span style={{fontWeight:700,color:col}}>{r.pct}%</span></td>
                      <td style={{color:'#6b7280',fontSize:'0.82rem'}}>{r.comment||'—'}</td>
                    </tr>
                  );
                })}
                {!data.recent?.length && <tr><td colSpan="6" style={{textAlign:'center',color:'#9ca3af',padding:'30px 0'}}>No recent assessments</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
